import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  AppBar,
  Toolbar,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { ReactComponent as LogoTagline } from '../assets/logo.svg';
import Grid from '@mui/material/Grid2';
import { useSearchParams, Navigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      setError('Reset token not found in URL.');
    }
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
        setError('Missing reset token.');
        return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: token, new_password: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message + ' Redirecting to login...');
        // Redirect to login after a short delay
        setTimeout(() => {
          setRedirect(true);
        }, 3000); 
      } else {
        throw new Error(data.detail || 'Failed to reset password.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (redirect) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: '#f8f9fe',
        position: 'relative',
        opacity: 0,
        animation: 'fadeIn 1s forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)'
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: { xs: 0.5, md: 1 },
            px: { xs: 2, md: 3 },
            minHeight: { xs: '56px', md: '64px' }
          }}
        >
          <LogoTagline style={{ height: '36px', cursor: 'pointer' }} onClick={() => window.location.href = '/'} />
          {/* Optional: Add Login/Signup buttons if needed */}
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 4, md: 8 },
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-50%',
            width: '200%',
            height: '100%',
            background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.03) 0%, transparent 70%)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
              borderRadius: '24px',
              border: '1px solid rgba(25, 118, 210, 0.1)',
              boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ fontWeight: 700, mb: 2, color: '#0A2540' }}
            >
              Reset Your Password
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#546E7A' }}>
              Enter your new password below.
            </Typography>
            
            {message && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

            {!token ? (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>Invalid or missing reset token.</Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}
              >
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading || !!message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#1976d2' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading || !!message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#1976d2' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end">
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!password || !confirmPassword || loading || !!message}
                  sx={{
                    py: 2,
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ResetPassword; 